/**
 * Single source of truth for the entretien-finance FAQ.
 * Used by layout.tsx (JSON-LD) AND page.tsx (visible <FaqSection>).
 */

import type { FaqItem } from "@/components/landing/FaqSection";

export const financeFaqFr: FaqItem[] = [
  {
    q: "Quels types d'entretiens finance sont couverts ?",
    a: "Banque d'investissement (M&A, ECM, DCM, Leveraged Finance), Private Equity, Asset Management, Hedge Funds, Corporate Finance, Audit & Transaction Services (Big 4). Chaque mode a ses questions types : DCF, LBO, comparables, accretion/dilution, brain teasers.",
  },
  {
    q: "Velyq m'aide-t-il pour les brain teasers ?",
    a: "Oui. Banque de 200+ brain teasers (logique, estimation, probabilités, énigmes classiques Wall Street) avec correction pas à pas. L'IA te challenge comme en vrai : si tu réponds vite sans raisonnement, elle te demande de détailler.",
  },
  {
    q: "Est-ce efficace pour Goldman Sachs, Morgan Stanley, Rothschild ?",
    a: "Oui. Les modes sectoriels utilisent des questions réelles rapportées par d'anciens candidats (banques bulge bracket US, élite boutiques FR comme Rothschild, Lazard, Messier & Associés, PJT Paris). Focus sur la rigueur technique et la structure de réponse.",
  },
  {
    q: "Je passe un entretien summer analyst, c'est adapté ?",
    a: "Oui, c'est même le use case #1. Les étudiants HEC, ESSEC, Dauphine, X utilisent Velyq pour leurs entretiens summer analyst en BNP CIB, SocGen CIB, Crédit Agricole CIB, JP Morgan, Citi. Mode junior avec questions DCF basique, WACC, EV vs Equity Value, et fit questions.",
  },
  {
    q: "Et pour un entretien Private Equity ?",
    a: "Mode PE avec LBO modeling guidé, case studies deal (analyse d'une cible, thèse d'investissement), questions sur returns (IRR, MOIC), et discussion d'un deal que tu as suivi. Adapté pour PAI, Ardian, Eurazeo, Astorg, BC Partners, et fonds mid-cap.",
  },
  {
    q: "Les questions sont-elles en français ou en anglais ?",
    a: "Les deux. La finance utilise beaucoup d'anglais même en France : Velyq te prépare au code-switching naturel (termes techniques EN, fit questions FR). Bascule 100% EN si tu postules à Londres, New York, Dubai ou Singapour.",
  },
  {
    q: "Combien de sessions pour être prêt ?",
    a: "Pour un super day banque d'investissement : 8-12 sessions Velyq sur 3-4 semaines. Pour un entretien PE : 15-20 sessions (les LBO prennent du temps à maîtriser). Le dashboard Velyq track tes scores par thème pour cibler tes faiblesses.",
  },
];

export const financeFaqEn: FaqItem[] = [
  {
    q: "What types of finance interviews does Velyq cover?",
    a: "Investment banking (M&A, ECM, DCM, Leveraged Finance), Private Equity, Asset Management, Hedge Funds, Corporate Finance, Audit & Transaction Services (Big 4). Each mode has its signature questions: DCF, LBO, comparables, accretion/dilution, brain teasers.",
  },
  {
    q: "Does Velyq help with brain teasers?",
    a: "Yes. 200+ brain teasers (logic, estimation, probability, Wall Street classics) with step-by-step corrections. The AI challenges you like a real interviewer: if you answer quickly without reasoning, it asks you to walk through it.",
  },
  {
    q: "Is it effective for Goldman Sachs, Morgan Stanley, Rothschild?",
    a: "Yes. Sector modes use real questions reported by past candidates (US bulge bracket banks, FR elite boutiques like Rothschild, Lazard, Messier & Associés, PJT Paris). Focus on technical rigor and answer structure.",
  },
  {
    q: "I have a summer analyst interview — is it a fit?",
    a: "Yes, it's the #1 use case. HEC, ESSEC, Dauphine, X students use Velyq for summer analyst interviews at BNP CIB, SocGen CIB, Crédit Agricole CIB, JP Morgan, Citi. Junior mode with basic DCF, WACC, EV vs Equity Value, and fit questions.",
  },
  {
    q: "What about Private Equity interviews?",
    a: "PE mode with guided LBO modeling, deal case studies (target analysis, investment thesis), returns questions (IRR, MOIC), and discussion of a deal you tracked. Built for PAI, Ardian, Eurazeo, Astorg, BC Partners, and mid-cap funds.",
  },
  {
    q: "Are questions in French or English?",
    a: "Both. Finance uses a lot of English even in France: Velyq trains you for natural code-switching (technical terms EN, fit questions FR). Switch to 100% EN if you're applying in London, New York, Dubai or Singapore.",
  },
  {
    q: "How many sessions to be ready?",
    a: "For an investment banking super day: 8-12 Velyq sessions over 3-4 weeks. For a PE interview: 15-20 sessions (LBOs take time to master). The Velyq dashboard tracks your scores by theme to target your weaknesses.",
  },
];
